import { Tooltip } from '@mui/material';
import { withStyles } from '@mui/styles';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { isEmbedded } from '@cyberint/common-utils';
import { Icons, MONITOR_SCREENS } from '@cyberint/dsc';

const { monitor } = MONITOR_SCREENS;
const breakPoint = isEmbedded ? '(min-width: 0)' : monitor.gte_medium;
const floating = '320px';
const logoExpandedWidth = '181px';
/* the offset is used to move the menu body (the transparent one) to the left
so that the mouse movement will remain in the context of the menu item
and not accidentally activate another adjacent menu */
export const offset = 15;
export const expandedEm = 264;
export const collapsedEm = 70;
const expanded = '320px';
const collapsed = '76px';
const floatingTop = '4px';
const floatingLeft = '13px';
const marginLeft = '15px';
const menuLeft = '25px';
// const marginRight = '12px';
const zIndex = 1001;

const CommonMenu = css`
  border-radius: var(--nav-submenu-radius);
  background-color: var(--nav-panel-surface-bg);
  box-shadow: var(--nav-submenu-shadow);
  backdrop-filter: blur(52px);
`;

const CommonScroll = css`
  ::-webkit-scrollbar {
    width: 2px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }
`;

export const NavMenuWrapper = styled.div`
  display: flex;
  flex-direction: row;
`;

const whiteLabelIconCss = css`
  path {
    fill: ${({ theme }) => theme?.colors?.secondary?.main};
  }

  rect {
    stroke: ${({ theme }) => theme?.colors?.secondary?.main};
  }
`;

export const FloatingMenu = styled(Icons.MenuFloating)`
  z-index: ${zIndex};
  cursor: pointer;
  position: absolute;
  top: ${floatingTop};
  left: ${marginLeft};
  @media ${breakPoint} {
    display: none;
  }

  ${props =>
    !props.isNonWhiteLabel &&
    css`
      ${whiteLabelIconCss}
    `};
`;

export const CloseMenu = styled.div`
  cursor: pointer;
  position: absolute;
  top: 18px;
  left: 18px;
  color: ${({ theme }) => theme?.colors?.primary?.contrast};
  @media ${breakPoint} {
    display: none;
  }
`;

const logoStyle = css`
  color: ${({ theme }) => theme?.colors?.navigationAccent};
  cursor: pointer;
`;

export const LogoSmallWrapper = styled.img.attrs({ className: 'nav-logo' })`
  margin-left: ${menuLeft};
  width: 32px;
  ${logoStyle}
`;

export const LogoWrapper = styled.img.attrs({ className: 'nav-logo' })`
  transform: scale(1.15);
  margin-bottom: 6px;
  margin-left: 60px;
  width: 76px;
  ${logoStyle}
`;

export const LogoCpWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 0 6px ${menuLeft};
  ${logoStyle}

  ${LogoWrapper} {
    margin: 0;
    transform: none;
    width: ${logoExpandedWidth};
    height: auto;
  }
`;

export const LogoTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 6px;
  padding-left: 4px;
`;

export const LogoName = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  letter-spacing: 0.2px;
`;

export const LogoSlogan = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.55);
  white-space: nowrap;
  letter-spacing: 0.2px;
`;

const tenantName = css`
  color: var(--nav-item-text);
  font-size: 14px;
  font-weight: 500;
`;

export const TenantsBaseWrapper = styled.div`
  color: var(--nav-item-text);

  .MuiInputBase-root {
    ${tenantName}
    &:before {
      display: none;
    }

    &:after {
      display: none;
    }
  }

  .MuiAutocomplete-noOptions,
  .MuiAutocomplete-endAdornment .MuiButtonBase-root {
    color: var(--nav-item-text);
  }

  .MuiAutocomplete-popper .MuiPaper-root {
    ${CommonMenu};

    /* Targets the listbox root by its stable MUI class rather than a bare "ul" selector: the
       virtualized listbox (TenantSelector.jsx) nests an unclassed inner <ul> of its own for
       react-window's content-sizing box, which a bare "ul" selector would also match and clamp,
       breaking the browser's native scrollbar against react-window's virtual scroll range. */
    .MuiAutocomplete-listbox {
      max-height: 230px;
      margin-right: 4px;
      //width: 200px;

      ${CommonScroll}

      /* CommonScroll's 2px track is tuned for short menus; this list can hold thousands of
         tenants and needs a wider, draggable thumb. A fixed thumb height (as elsewhere in this
         file) doesn't fit here since it wouldn't reflect how much of a 2000-tenant list the
         visible ~6 rows represent -- leave the thumb unset so the browser sizes it
         proportionally to the real (virtualized) scroll range. */
      ::-webkit-scrollbar {
        width: 6px;
      }
    }
  }
`;

export const TenantsWrapper = styled(TenantsBaseWrapper)`
  position: relative;
  .tenants-loading {
    position: absolute;
    right: -5px;
    top: -5px;
  }

  background-color: ${({ theme }) => theme?.colors?.tenantInputBg};
  /* outline, not border: matches the breadcrumb tenant crumb and never shifts layout. */
  outline: 1px solid var(--nav-tenant-border-color);
  outline-offset: -1px;
  border-radius: var(--breadcrumb-tag-radius);
  padding: 0 5px 0 8px;
  margin: 5px ${menuLeft} 10px;

  .MuiAutocomplete-popper .MuiPaper-root {
    background-color: ${({ theme }) => theme?.colors?.primary?.transparent};
  }
`;

export const TenantName = styled.div`
  display: flex;
  gap: 6px;

  ${({ $hidden }) =>
    $hidden &&
    css`
      position: absolute;
      top: -5000px;
    `}
`;

export const TenantBreadcrumbWrapper = styled(TenantsBaseWrapper)`
  display: flex;
  flex-direction: row;
  height: 15px;

  ${TenantName} {
    line-height: 15px;
    font-size: 12px;
  }

  .MuiAutocomplete-root {
    .MuiInputBase-root {
      font-size: 12px;
      line-height: 15px;
    }

    .MuiAutocomplete-inputRoot {
      padding-right: 15px !important;
    }

    .MuiInput-root {
      .MuiInput-input {
        padding: 0;
        height: 15px;
      }
    }

    .MuiAutocomplete-endAdornment {
      right: 0;
      width: 18px;

      svg {
        width: 18px;
      }
    }
  }

  .MuiAutocomplete-popper .MuiPaper-root {
    width: 220px;
  }
`;

export const OptionText = styled.div`
  width: fit-content;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  /* Flex items get an automatic min-size of 0 while overflow:hidden. Pin it so this box never
     grows past its clamped fit-content width regardless of overflow, which would otherwise force
     the row to grow and shove the action icons over. This box is only the clipping viewport --
     see the inner span below for the part that actually slides. */
  min-width: 0;

  /* --marquee-distance/--marquee-duration are set inline by handleMarqueeHover (measured from the
     real overflow amount and a constant px/s speed, so a barely-overflowing name doesn't crawl
     and a very long one doesn't fly by) rather than hardcoded, since a fixed duration is either
     too fast to read on long names or takes forever on short ones. */
  > span {
    display: inline-block;
    transition: transform var(--marquee-duration, 0s) linear;
  }

  &:hover > span,
  &:focus > span {
    transform: translateX(calc(-1 * var(--marquee-distance, 0px)));
    transition-delay: 0.8s;
  }
`;

export const TenantOptionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ImpersonateIcon = styled.span`
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  color: var(--cadetGray);
  padding: 2px 4px;

  &:hover {
    color: var(--text-on-surface);
  }

  ${({ $selected }) =>
    $selected &&
    css`
      /* The Icons.Visibility svg is a clickable DSC icon, so .dsc-icon-wrapper sets its own color
         (--icon-default-clickable-color) with higher specificity than anything set here on the
         outer span — and in the legacy theme that color is the exact same mint as the selected-row
         background, making the icon invisible. Override on the wrapper itself with the fill's
         companion contrast var, same fix TenantInfoIcon uses for the same background. */
      && .dsc-icon-wrapper {
        color: var(--nav-item-selection-indicator-contrast);

        &:hover {
          opacity: 0.8;
        }
      }
    `}
`;

export const TooltipContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 12px;

  > div {
    display: flex;
    gap: 4px;

    > span:first-child {
      font-weight: 500;
    }
  }
`;

export const CopyableId = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const TenantInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;

  ${({ $selected }) =>
    $selected &&
    css`
      /* DSC sets its own color on the icon wrapper, so override it there to stay visible on the
         --nav-item-selection-indicator selected-row fill, whatever color that fill is. */
      && .dsc-icon-wrapper {
        color: var(--nav-item-selection-indicator-contrast);
      }
    `}
`;

const getTenantOptionCss = selected => css`
  color: ${selected ? 'var(--nav-item-selection-indicator-contrast) !important' : 'var(--text-on-surface)'};
  font-size: 14px !important;
  font-weight: ${selected ? '700' : '300'};
  background-color: ${selected && 'var(--nav-item-selection-indicator) !important'};
  border-top-right-radius: 15px;
  border-bottom-right-radius: 15px;
  width: calc(100% - 5px);
  padding-right: 3px !important;

  &.MuiAutocomplete-option.Mui-focused {
    font-weight: 700;
    color: ${selected ? 'var(--nav-item-selection-indicator-contrast) !important' : 'var(--text-on-surface)'};
    background-color: ${selected ? 'var(--nav-item-selection-indicator) !important' : 'transparent !important'};
  }

  &&& {
    display: flex;
    justify-content: space-between;
  }
`;

export const TenantOption = styled.div`
  ${({ $selected }) => getTenantOptionCss($selected)};
`;

export const NavigationTenantOption = styled(TenantOption)`
  ${({ $selected }) => getTenantOptionCss($selected)};
  color: ${({ $selected, theme }) => $selected && `${theme?.colors?.selectedTenant} !important`};
  background-color: ${({ $selected, theme }) => $selected && `${theme?.colors?.selectedTenantBg} !important`};

  &.MuiAutocomplete-option.Mui-focused {
    color: ${({ $selected, theme }) => $selected && `${theme?.colors?.selectedTenant} !important`};
    background-color: ${({ $selected, theme }) =>
      $selected ? `${theme?.colors?.selectedTenantBg} !important` : 'transparent !important'};
  }
`;

export const UserInfoWrapper = styled.div`
  align-self: center;
  width: 40px;
  height: 40px;
  margin: 8px 0 0 0;
  user-select: none;

  .userSettings {
    background-color: var(--user-avatar-bg, ${({ theme }) => theme?.colors?.secondary?.main});
    border-radius: 50px;
    height: 32px;
    width: 32px;
    cursor: pointer;

    .user-first-char {
      color: ${({ theme }) => theme?.colors?.userInfoContrast};
      font-size: 14px;
      font-weight: bold;
      text-align: center;
      position: relative;
      height: 32px;
      width: 32px;
      text-transform: uppercase;
      vertical-align: middle;
      line-height: 32px;
    }

    .user-details-container {
      margin-left: 20px;
      position: absolute;
      left: 38px;
      bottom: 65px;
      min-width: 182px;
      width: unset;

      ${CommonMenu};
      background-color: ${({ theme }) => theme?.colors?.primary?.transparent};

      .details {
        border-bottom: unset;

        .name {
          color: ${({ theme }) => theme?.colors?.primary?.contrast};
          font-size: 16px;
          font-weight: 500;
        }

        .email {
          //color: #082f35; /** Doesn't look good ontop of dark menu*/
          color: ${({ theme }) => theme?.colors?.primary?.contrastHighlight};

          font-size: 12px;
          font-weight: 400;
        }
      }

      .actions {
        text-transform: unset;
        font-size: 14px;

        .legacy-back {
          border-top: 1px var(--codet-gray) solid;
        }

        > * {
          color: ${({ theme }) => theme?.colors?.primary?.contrast};

          :hover {
            color: ${({ theme }) => theme?.colors?.primary?.contrastHighlight};
            background-color: unset;
            font-weight: 700;
          }
        }
      }
    }
  }
`;

export const ControlWrapper = styled.div`
  text-align: center;
  align-self: center;

  a {
    position: relative;
  }
`;

export const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-self: center;

  margin: auto 0 30px 0;
  gap: 22px;

  svg {
    color: var(--nav-item-icon-color);
    cursor: pointer;
  }
`;

export const NavMenuLabel = styled.div`
  padding-left: 10px;
  font-size: 14px;
  height: 40px;
  display: flex;
  align-items: center;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const Badge = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: none;
  width: var(--nav-badge-width);
  height: var(--nav-badge-height);
  padding: var(--nav-badge-padding);
  border-radius: var(--nav-badge-radius);
  background-color: ${({ $newBadgeBg }) => $newBadgeBg};
  /* Not --secondary-contrast-highlight: the alerts count badge shares it and needs white. */
  color: var(--nav-badge-color, ${({ theme }) => theme?.colors?.secondary?.contrastHighlight});
  font-weight: var(--nav-badge-weight);
  font-size: 10px;
  margin: 0 7px;
  ${({ $small }) =>
    $small &&
    css`
      position: absolute;
      top: -1px;
      right: -11px;
      width: var(--nav-badge-small-size);
      height: var(--nav-badge-small-size);
      padding: var(--nav-badge-small-padding);
      margin: 0;
    `}
`;

export const NavMenuExpandContainer = styled.div`
  cursor: pointer;
  width: 30px;
  height: 30px;
  padding: 5px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const NavMenuExpand = styled.div`
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.navigationAccent};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  transform: ${({ $expanded }) => $expanded && 'rotate(90deg)'};
  transition: transform 0.3s;
`;

export const LinkWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 2px 0 4px;
`;

export const NavSubLinks = styled.div`
  margin-left: 34px;
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  opacity: ${({ $visible }) => ($visible ? '1' : '0')};
  max-height: ${({ $visible }) => ($visible ? '400px' : '0')};
  //height: ${({ $visible }) => ($visible ? 'auto' : '0')};
  transition: max-height 0.3s, opacity 0.2s, visibility 0.2s ease-in-out;

  ${LinkWrapper} {
    max-height: ${({ $visible }) => ($visible ? '50px' : '0')};
    transition: max-height 0.3s;
  }
`;

export const ActiveMenuStripe = styled.div`
  height: 40px;
  width: var(--nav-active-stripe-width);
  border-radius: var(--nav-active-stripe-radius);
  background-color: transparent;
`;

export const NavMenuLink = styled(NavLink)`
  position: relative;
  width: 100%;
  height: 40px;
  color: ${({ theme }) => theme?.colors?.primary?.contrast};
  text-decoration: none;
  display: flex;
  flex: 1;
  font-weight: 400;
  align-items: center;
  flex-direction: row;

  &.active:not(.none-pivot),
  :hover {
    font-weight: 700;
    color: ${({ theme }) => theme?.colors?.primary?.contrastHighlight};
    ${NavMenuLabel} {
      font-weight: inherit;
      color: inherit;
    }
  }

  &.active:not(.none-pivot) ${ActiveMenuStripe} {
    background-color: ${({ theme }) => theme?.colors?.secondary?.main};
  }

  /* Nav icons read the global nav icon variables rather than inheriting the link colour, so a
     theme can restyle every nav icon in one place. The :root defaults are the link colours, so
     the rendering is unchanged. */
  .dsc-icon-wrapper {
    color: var(--nav-item-icon-color);
  }

  &.active:not(.none-pivot) .dsc-icon-wrapper {
    color: var(--nav-item-text-highlighted);
  }

  //&.active {
  //  div#label {
  //    color: var(--darkMode-white);
  //  }
  //}
`;

export const NavMenuHeader = styled(NavMenuLabel)`
  padding: 5px 0 11px 15px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--codet-gray);
  cursor: default;
`;

export const NavSubMenu = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme?.colors?.primary?.semiTransparent};
  overflow: auto;
  height: 100%;

  ${NavMenuLink} {
    margin-left: 16px;
    text-transform: unset;
    padding-left: unset;
  }
`;

export const NavTooltip = withStyles({
  tooltip: {
    backgroundColor: 'transparent !important',
    position: 'unset !important',
    border: 'unset !important',
    overflow: 'unset !important',
    pointerEvents: 'unset !important'
  }
})(Tooltip);

export const NavSubLinksTooltip = styled.div`
  display: block;
  ${CommonMenu};
  /* The theme value is always set for the default theme, so --nav-submenu-bg must come first. */
  background-color: var(--nav-submenu-bg, ${({ theme }) => theme?.colors?.primary?.semiTransparent});
  margin-left: 7px;
  margin-top: -4px;
  overflow: hidden;

  ${NavSubLinks} {
    border-radius: var(--nav-submenu-radius);
    padding-right: 10px;
  }

  ${NavMenuLink} {
    height: 35px;
    border-radius: ${({ $single }) => $single && 'var(--nav-submenu-radius)'};

    &.active.none-pivot {
      ${ActiveMenuStripe} {
        visibility: hidden;
        background-color: transparent;
      }
    }
  }

  ${NavMenuLabel} {
    padding-right: 10px;
  }

  > ${NavMenuLabel} {
    padding: 5px 0 11px 15px;
    border-bottom: 1px solid var(--nav-submenu-divider-color);
    font-weight: var(--nav-submenu-title-weight);
    text-transform: var(--title-text-transform);
    color: var(--nav-submenu-title-color);
    cursor: default;
  }
`;

export const NavMenPlaceholder = styled.div`
  background-color: var(--nav-menu-bg);
  width: ${collapsed};
  min-width: ${collapsed};
  height: 100%;
  display: none;
  @media ${breakPoint} {
    display: unset;
  }
`;

const userSettings = css`
  .userSettings {
    background-color: var(--user-avatar-bg, ${({ theme }) => theme?.colors?.secondary?.main});

    .user-first-char {
      color: ${({ theme }) => theme?.colors?.userInfoContrast};
    }

    .user-details-container {
      background-color: ${({ theme }) => theme?.colors?.primary?.transparent};

      .name {
        color: ${({ theme }) => theme?.colors?.primary?.contrast};
      }

      .email {
        color: ${({ theme }) => theme?.colors?.primary?.contrastHighlight};
      }

      .actions > * {
        color: ${({ theme }) => theme?.colors?.primary?.contrast};

        :hover {
          color: ${({ theme }) => theme?.colors?.primary?.contrastHighlight};
        }
      }
    }
  }
`;

export const NavMenu = styled.div`
  display: ${({ $open }) => ($open ? 'flex' : 'none')};
  position: absolute;
  flex-direction: column;
  z-index: ${zIndex};
  top: ${floatingTop};
  left: ${floatingLeft};
  bottom: ${floatingTop};
  width: ${floating};
  min-width: ${floating};
  transition: width 0.3s, min-width 0.1s;

  ${props => props.isNonWhiteLabel && 'border: 1px var(--mainGreen) solid'};
  background-color: ${({ theme }) => theme?.colors?.primary?.main};
  border-radius: var(--main-card-radius);
  box-shadow: 0 4px 64px 0 var(--black-400);
  backdrop-filter: blur(25px);
  padding-top: 40px;

  .dsc-toggle {
    margin: 5px 0 5px ${menuLeft};
  }

  ${LogoWrapper} {
    margin-left: ${menuLeft};
    transform: none;
    width: ${logoExpandedWidth};
    height: auto;
  }

  ${TenantName} {
    margin: 7px 0 5px 0;
    ${tenantName}
  }

  @media ${breakPoint} {
    display: flex;
    padding: 15px 0 10px 0;
    background-color: ${({ theme }) => theme?.colors?.primary?.main};
    width: ${collapsed};
    min-width: ${collapsed};
    top: 0;
    left: 0;
    bottom: 0;
    border: unset;
    border-radius: unset;
    box-shadow: unset;
    backdrop-filter: unset;

    ${LogoWrapper} {
      margin-left: ${menuLeft};
      margin-bottom: 6px;
      transform: none;
      width: ${logoExpandedWidth};
      height: 36px;
    }

    ${LogoCpWrapper} {
      margin-left: 15px;
      margin-top: 12px;
    }

    ${TenantsWrapper} {
      visibility: hidden;
      width: calc(100% - 50px);
      height: 0;
    }

    ${LinkWrapper} {
      width: 35px;
    }

    ${NavMenuLabel}, ${NavMenuExpand} {
      display: none;
    }

    ${ControlsWrapper} {
      flex-direction: column-reverse;
      align-self: unset;
      gap: 20px;
      margin: auto 0 ${marginLeft} -6px;
    }

    ${UserInfoWrapper} {
      margin: 0 0 0 8px;
      ${userSettings}
    }

    ${({ $open }) =>
      $open &&
      css`
        width: ${expanded};
        min-width: ${expanded};
        background-color: ${({ theme }) => theme?.colors?.primary?.main};
        box-shadow: 0 4px 64px 0 var(--black-400);
        backdrop-filter: blur(15px);

        ${TenantsWrapper} {
          visibility: visible;
          height: unset;
          width: auto;
        }

        ${LinkWrapper} {
          width: unset;
        }

        ${NavMenuExpand} {
          display: flex;
        }

        ${NavMenuLabel} {
          display: flex;
        }

        ${ControlsWrapper} {
          flex-direction: row;
          gap: 22px;
          margin: auto auto 10px;
        }

        ${UserInfoWrapper} {
          margin: 8px 0 0 0;
        }
      `}
  }
`;

export const NavMenuEm = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  z-index: ${zIndex};
  transition: width 0.3s, min-width 0.1s;
  background-color: ${({ theme }) => theme?.colors?.primary?.main};
  padding: 15px 0 10px 0;
  width: ${collapsedEm}px;
  min-width: ${collapsedEm}px;

  ${TenantName} {
    margin: 7px 0 5px 0;
    ${tenantName}
  }

  // ${LinkWrapper} {
  //   width: 35px;
  // }

  ${LogoWrapper} {
    margin-left: ${menuLeft};
    margin-top: 0;
    margin-bottom: 6px;
    width: ${logoExpandedWidth};
    height: auto;
    transform: none;
  }

  ${TenantsWrapper} {
    visibility: hidden;
    width: calc(100% - 50px);
    margin: 5px ${marginLeft} 10px;
  }

  ${NavMenuLabel}, ${NavMenuExpand} {
    display: none;
  }

  ${ControlsWrapper} {
    flex-direction: column-reverse;
    align-self: unset;
    gap: 20px;
    margin: auto 0 ${marginLeft} -6px;
  }

  ${UserInfoWrapper} {
    margin: 0 0 0 8px;
    ${userSettings}
  }

  ${Badge} {
    right: 10px;
  }

  ${({ $open }) =>
    $open &&
    css`
      width: ${expandedEm}px;
      min-width: ${expandedEm}px;

      ${TenantsWrapper} {
        visibility: visible;
        margin-left: ${menuLeft};
        margin-top: 5px;
      }

      ${LinkWrapper} {
        width: unset;
      }

      ${NavMenuExpand} {
        display: flex;
      }

      ${NavMenuLabel} {
        display: flex;
      }

      ${ControlsWrapper} {
        flex-direction: row;
        gap: 22px;
        margin: auto auto 10px;
      }

      ${UserInfoWrapper} {
        margin: 8px 0 0 0;
      }
    `}
`;

export const NavMenuToggle = styled(Icons.MenuToggle)`
  position: absolute;
  width: 30px;
  height: 30px;
  top: 30px;
  right: -12px;
  z-index: ${zIndex};
  cursor: pointer;
  border-radius: 5px;
  transform: ${({ $expanded }) => !$expanded && 'rotate(180deg)'};
  visibility: hidden;
  @media ${breakPoint} {
    visibility: visible;
  }

  rect:first-of-type {
    fill: ${({ theme }) => theme?.colors?.primary?.dark || 'var(--app-bg)'};
  }
  rect:nth-of-type(2) {
    stroke: ${({ theme }) => theme?.colors?.secondary?.main || 'var(--mainGreen)'};
  }
  path {
    stroke: ${({ theme }) => theme?.colors?.secondary?.main || 'var(--mainGreen)'};
  }
`;

export const LinksContainer = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  margin: 0 1px 10px ${marginLeft};
  flex-grow: 1;

  ${CommonScroll}
  ::-webkit-scrollbar-thumb {
    height: 180px; /** Will not work if the scroll is not visible at init */
    visibility: hidden;
  }

  :active::-webkit-scrollbar-thumb,
  :focus::-webkit-scrollbar-thumb,
  :hover::-webkit-scrollbar-thumb {
    visibility: visible;
  }
`;

export const LinkAndExpand = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  &:hover {
    div#label {
      color: ${({ theme }) => theme?.colors?.primary?.contrastHighlight};
    }
  }
`;

export const NavMenuIconContainer = styled.div`
  width: 30px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
`;

export const NavMenuIcon = styled.div`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
